import React, {
	useEffect, useMemo, useRef,
} from 'react';
import { FormikHelpers, useFormik } from 'formik';
import { Placeholder } from 'rsuite';
import { Trans, useTranslation } from 'react-i18next';
import { MutationFunction, useMutation } from '@apollo/client';
import {
	alephToast,
	Input,
	NotificationToast,
	NotificationType,
	getObjectsDiff,
	useDebouncedMemo,
	ButtonSize,
	ComboBox,
	isReddit,
	isSpotify,
	AvatarSize,
	getNewAvatar,
	getRandomMainColor,
	cn,
	MenuSelectType,
	IMenuOption,
} from '@aleph/shared-components';
import useRedditUserMutationStorage from 'wb-reddit/app/routes/users/mutator/useRedditUserMutationStorage';
import {
	InviteUserToGroupsDocument,
	UpdateRedditUserDocument,
	UserAccountLevel,
} from 'wb-reddit/graphql/generated';
import {
	canUserEditGroup,
	getUpdateUserValidationSchema,
	MutatingRedditUser,
	UserGroupOption,
} from 'wb-reddit/app/routes/users/mutator/utils';
import ProfileImageUploader from 'wb-reddit/app/common/components/profileImageUploader';
import GroupListItem from 'wb-reddit/app/routes/users/components/groupListItem';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import AccessBadge from 'wb-reddit/app/common/components/accessBadge';
import { getRedditUserLevelName } from 'wb-reddit/app/routes/users/utils';
import EntityEditInputSuffix from 'wb-reddit/app/common/components/entityEditInputSuffix';
import { MutatorProps } from 'wb-reddit/app/routes/users/mutator/container';
import UsersTeamsAutocomplete from 'wb-reddit/app/routes/users/mutator/usersTeamsAutocomplete';
import { getUserAdAccountAccess } from 'wb-spotify/app/common/utils';
import {
	UserAccountLevel as AuthGraphUserAccountLevel,
	AuthGraphUpdateUserDocument,
	User,
	AuthGraphUpdateUserMutation,
	AuthGraphUpdateUserMutationVariables,
	AuthGraphAddUserToUserGroupDocument,
	AuthGraphAddUserToUserGroupMutation,
	AuthGraphAddUserToUserGroupMutationVariables,
	WbnPlatform,
} from 'wb-spotify/graphql/authgraph/generated';
import RedditEntityImage from 'wb-reddit/app/common/components/entityImage';
import useSpotifyRolePermissions from 'wb-spotify/app/common/hooks/useSpotifyPermissions';
import { AuthGraphAssignCredentialsDocument } from 'wb-reddit/graphql/authgraph/generated';

const RedditUsersEditMutator = ({ updateRow, setShouldShowSaveBeforeExit }: MutatorProps) => {
	const { t } = useTranslation();
	const { mutationUser, setMutationUser } = useRedditUserMutationStorage();
	const initialValues = useRef(
		isReddit()
			? mutationUser
			: {
				...mutationUser,
				userEmailConfirmation: mutationUser?.userEmail,
			},
	) as React.MutableRefObject<MutatingRedditUser>;

	const [inviteUserToGroups, { loading: invitingUser }] = useMutation(
		isReddit()
			? InviteUserToGroupsDocument
			: AuthGraphAddUserToUserGroupDocument,
		{
			errorPolicy: 'all',
		},
	);

	const [onUpdateUser] = useMutation(isReddit() ? UpdateRedditUserDocument : AuthGraphUpdateUserDocument, {
		errorPolicy: 'all',
	});

	const [updateCredentials, { loading: assigningCredentials }] = useMutation(AuthGraphAssignCredentialsDocument);

	const currentPlatformUser = useUserMetaData();

	const roleOptions = useMemo(() => (isReddit()
		? Object.values(UserAccountLevel)?.map((level) => ({
			label: getRedditUserLevelName(level),
			value: level,
		}))
			.filter(({ value }) => value !== UserAccountLevel.Unknown)
			.filter(
				// only Admin users can create Admin users
				({ value }) => value !== UserAccountLevel.Admin
          || currentPlatformUser?.userAccountLevel === UserAccountLevel.Admin,
			)
		: Object.values(AuthGraphUserAccountLevel)?.map((level) => ({
			label: getRedditUserLevelName(level),
			value: level,
		})) ?? []), []);

	// @ts-ignore
	const userAccess = useMemo(() => (isReddit() ? (mutationUser?.userAccess ?? [])
		: getUserAdAccountAccess(mutationUser as unknown as User)), [mutationUser]);

	const onConfirmMutation = async (values: MutatingRedditUser, { setErrors }: FormikHelpers<MutatingRedditUser>) => {
		// Need to send only dirty
		const {
			userGroups, userEmailConfirmation, userPasswordConfirmation, ...restOfValues
		} = values;
		const {
			userGroups: initialUserGroups,
			pendingInvites: initialPendingInvites,
			userEmailConfirmation: initialUserEmailConfirmation,
			userPasswordConfirmation: initialUserPasswordConfirmation,
			...restOfInitialValues
		} = initialValues.current;

		const dirty = getObjectsDiff(restOfValues, restOfInitialValues, true);

		const dirtyFieldKeys = Object.keys(dirty);

		const variables = dirtyFieldKeys.reduce((accu, field) => {
			accu[field] = values[field];

			return accu;
		}, {});

		if (isReddit()) {
			const responseData = await onUpdateUser({
				variables: {
					userId: values?.userId ?? '',
					...variables,
				},
			});

			// @ts-ignore
			const validationErrors = Object.keys(responseData.errors?.[0]?.form ?? {}).reduce((accu, field) => {
				// @ts-ignore
				accu[field] = t(responseData.errors?.[0]?.form?.[field]?.message ?? t('sc: unknown err', 'Unknown error'));

				return accu;
			}, {});

			setErrors(validationErrors);

			if (!Object.keys(validationErrors).length) {
				if (responseData.errors) {
					alephToast(
						<NotificationToast
							type={NotificationType.Error}
							title={
								<div className="text-white">
									{t('re: failed to update user', 'Failed to update user')}
								</div>
							}
							description={t(responseData?.errors?.[0]?.message ?? 'Unknown error')}
						/>,
						{ position: 'bottom-left' },
					);

					return;
				}

				if (responseData.data?.updateUser) {
					updateRow?.({
						// @ts-ignore TODO combine fields from mutation
						row: responseData.data?.updateUser,
					});
				}

				alephToast(
					<NotificationToast
						type={NotificationType.Success}
						title={
							<div className="text-white">
								<Trans
									i18nKey="sc: auth.user_updated"
									defaults="User <strong>{{name}}</strong> successfully updated."
									values={{ name: values.userName }}
								>
									User <strong>{values.userName}</strong> successfully updated.
								</Trans>
							</div>
						}
					/>,
					{ position: 'bottom-left' },
				);
				setMutationUser(undefined);
			}

			return;
		}

		const authGraphOnUpdateUser = onUpdateUser as MutationFunction<AuthGraphUpdateUserMutation, AuthGraphUpdateUserMutationVariables>;

		const responseData = await authGraphOnUpdateUser({
			variables: {
				userId: values?.userId ?? '',
				...variables,
			},
		});

		if (isSpotify() && values.userAccountLevel !== initialValues.current.userAccountLevel) {
			await updateCredentials({
				variables: {
					userId:      values.userId ?? '',
					WBNPlatform: WbnPlatform.Spotify,
				},
			});
		}

		// @ts-ignore
		const validationErrors = Object.keys(responseData.errors?.[0]?.form ?? {}).reduce((accu, field) => {
			// @ts-ignore
			accu[field] = t(responseData.errors?.[0]?.form?.[field]?.message ?? t('sc: unknown err', 'Unknown error'));

			return accu;
		}, {});

		setErrors(validationErrors);

		if (!Object.keys(validationErrors).length) {
			if (responseData.errors) {
				alephToast(
					<NotificationToast
						type={NotificationType.Error}
						title={
							<div className="text-white">
								{t('re: failed to update user', 'Failed to update user')}
							</div>
						}
						description={t(responseData?.errors?.[0]?.message ?? 'Unknown error')}
					/>,
					{ position: 'bottom-left' },
				);

				return;
			}

			if (responseData.data?.updateUser) {
				updateRow?.({
					// @ts-ignore TODO combine fields from mutation
					row: responseData.data?.updateUser,
				});
			}

			alephToast(
				<NotificationToast
					type={NotificationType.Success}
					title={
						<div className="text-white">
							<Trans
								i18nKey="sc: auth.user_updated"
								defaults="User <strong>{{name}}</strong> successfully updated."
								values={{ name: values.userName }}
							>
								User <strong>{values.userName}</strong> successfully updated.
							</Trans>
						</div>
					}
				/>,
				{ position: 'bottom-left' },
			);
			setMutationUser(undefined);
		}
	};

	const {
		values,
		handleChange,
		handleBlur,
		errors,
		touched,
		submitForm,
		setFieldValue,
		isSubmitting,
		resetForm,
	} = useFormik({
		// @ts-ignore
		initialValues: isReddit()
			? mutationUser
			: {
				...mutationUser,
				userEmailConfirmation: mutationUser?.userEmail,
			},
		initialTouched: isReddit()
			? {
				userProfilePicture: !!(mutationUser as MutatingRedditUser)?.userProfilePicture,
			}
			: undefined,
		validationSchema: getUpdateUserValidationSchema(),
		onSubmit:         onConfirmMutation,
	});

	const dirtyFields = useDebouncedMemo(
		() => {
			/**
			 * Exclude the user fields as they are changed inline, without submitting the form
			 */
			const { userGroups, pendingInvites, ...restOfValues } = values;
			const {
				userGroups: initialUserGroups,
				pendingInvites: initialPendingInvites,
				...restOfInitialValues
			} = initialValues.current;

			const dirty = getObjectsDiff(restOfValues, restOfInitialValues, true);

			// TODO remove when profile picture would be ready on BE
			if (isSpotify()) {
				delete dirty.userProfilePicture;
			}

			setShouldShowSaveBeforeExit(!!Object.keys(dirty).length);

			return dirty;
		},
		[values],
		150,
	);

	useEffect(() => {
		setMutationUser(values);
	}, [values]);

	const onUploadProfileImage = (newImage) => {
		setFieldValue('userProfilePicture', newImage);

		submitForm();
	};

	const nameInitials = `${values.userName?.trim()?.[0] ?? ''}${values.userSurname?.trim()?.[0] ?? ''}`.toUpperCase();
	const imageColor = useMemo(() => getRandomMainColor(), []);

	useEffect(() => {
		if (!touched.userProfilePicture && isReddit()) {
			getNewAvatar({ entityType: 'User', background: imageColor, initials: nameInitials })
				.then((newImage) => {
					setFieldValue('userProfilePicture', newImage);
				});
		}
	}, [nameInitials]);

	const onInviteUserToGroup = async (option: UserGroupOption) => {
		if (!values.userId || !option?.value) {
			return;
		}

		if (isReddit()) {
			const { errors: inviteErrors, data: inviteData } = await inviteUserToGroups({
				variables: {
					userId:       values.userId,
					// @ts-ignore
					userGroupIds: [option?.value],
				},
			});

			if (inviteErrors) {
				alephToast(
					<NotificationToast
						type={NotificationType.Error}
						title={
							t(
								're: something went wrong while inviting user to group',
								'Something went wrong while inviting user to group',
							)
						}
					/>,
				);

				return;
			}

			alephToast(
				<NotificationToast
					type={NotificationType.Success}
					title={
						<Trans
							i18nKey="re: user invited to group"
							defaults="User <strong>{{userName}}</strong> has been invited to team <strong>{{userGroupName}}</strong>"
							values={{
								userName:      values.userName,
								userGroupName: option.label,
							}}
						>
							User <strong>{values.userName}</strong> has been invited to team <strong>{option.label}</strong>
						</Trans>
					}
				/>,
			);

			// @ts-ignore TODO
			setFieldValue('pendingInvites', inviteData?.linkUserGroupsToUser?.pendingInvites ?? []);
			const newUserGroups = [
				...(mutationUser?.userGroups ?? []),
				{
					...option,
					userGroupName: option.label,
				},
			];

			updateRow?.({
				row: {
					// @ts-ignore TODO
					...mutationUser,
					// @ts-ignore TODO
					pendingInvites: inviteData?.linkUserGroupsToUser?.pendingInvites ?? [],
					// @ts-expect-error: mutationUser is the same as UserRow, all of the fields are defined
					userGroups:     newUserGroups,
				},
			});

			setFieldValue('userGroups', newUserGroups);

			return;
		}

		const inviteUserToGroupMutation = inviteUserToGroups as MutationFunction<AuthGraphAddUserToUserGroupMutation, AuthGraphAddUserToUserGroupMutationVariables>;

		const { errors: inviteErrors } = await inviteUserToGroupMutation({
			variables: {
				userId:      values.userId,
				userGroupId: option?.value,
			},
		});

		if (inviteErrors) {
			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title={
						t(
							're: something went wrong while inviting user to group',
							'Something went wrong while inviting user to group',
						)
					}
				/>,
			);

			return;
		}

		alephToast(
			<NotificationToast
				type={NotificationType.Success}
				title={
					<Trans
						// TODO: change translation to spotify; user has been added to group
						i18nKey="re: user invited to group"
						defaults="User <strong>{{userName}}</strong> has been invited to team <strong>{{userGroupName}}</strong>"
						values={{
							userName:      values.userName,
							userGroupName: option.label,
						}}
					>
						User <strong>{values.userName}</strong> has been invited to team <strong>{option.label}</strong>
					</Trans>
				}
			/>,
		);

		const newUserGroups = [
			...(mutationUser?.userGroups ?? []),
			{
				...option,
				userGroupName: option.label,
			},
		];

		updateRow?.({
			row: {
				...mutationUser,
				// @ts-expect-error: mutationUser is the same as UserRow, all of the fields are defined
				userGroups: newUserGroups,
			},
		});

		setFieldValue('userGroups', newUserGroups);
	};

	const revokeUserGroup = (group: UserGroupOption) => {
		setFieldValue(
			'userGroups',
			values.userGroups?.filter((currGroup) => currGroup.value !== group.value) ?? [],
		);

		updateRow?.({
			row: {
				...mutationUser,
				// @ts-expect-error: mutationUser is the same as UserRow, userGroups is also the same as the row,
				// so we just filter out the removed group
				userGroups: mutationUser?.userGroups?.filter((currGroup) => currGroup.value !== group.value) ?? [],
			},
		});
	};

	const { canEditUser: spotifyCanEditUser } = useSpotifyRolePermissions();
	const platformUser = useUserMetaData();
	const canEditUser = useMemo(
		() => {
			if (isReddit()) {
				return platformUser?.isAdmin;
			}

			return spotifyCanEditUser;
		},
		[spotifyCanEditUser, platformUser],
	);

	const isUpdatingUser = isSubmitting || assigningCredentials;

	return (
		<div className="flex flex-1 min-w-full px-[40px] pb-0 pt-[40px] gap-[40px]">
			<div className="flex-1 w-[25%] flex justify-center">
				{isReddit()
					? (
						<ProfileImageUploader
							profileImage={values.userProfilePicture}
							onUploadProfileImage={onUploadProfileImage}
							type="User"
							readOnly={!canEditUser}
							loading={isUpdatingUser}
						/>
					)
					// On authgraph, we don't have a profile image, so we use a fallback icon for now
					: (
						<div className={
							cn(
								'w-[167px] h-[167px] rounded-full bg-blue-200 text-blue',
								'flex items-center justify-center relative',
							)}
						>
							<RedditEntityImage
								entity={values}
								size={AvatarSize.ExtraLarge}
							/>
						</div>
					)
				}
			</div>
			<div className="flex-[2] min-w-[50%] w-[50%] flex flex-col gap-[24px]
					px-[2px] pb-[150px]"
			>
				<div className="font-semibold text-[14px]">
					{t('re: user general information', 'GENERAL INFORMATION')}
				</div>
				<div className="flex gap-[16px]">
					<Input
						className="flex-1"
						id="userName"
						label={t('re: user first name', 'First name')}
						onChange={handleChange}
						value={values.userName ?? ''}
						validationError={touched.userName ? errors.userName : undefined}
						onBlur={handleBlur}
						disabled={!canEditUser}
						suffix={
							<EntityEditInputSuffix
								loading={isUpdatingUser}
								onConfirm={submitForm}
								onCancel={() => resetForm({
									values: {
										...values,
										userName: initialValues.current.userName,
									},
								})}
								showSuffix={dirtyFields.userName}
							/>
						}
					/>

					<Input
						className="flex-1"
						id="userSurname"
						label={t('re:user last name', 'Last name')}
						onChange={handleChange}
						value={values?.userSurname ?? ''}
						validationError={touched.userSurname ? errors.userSurname : undefined}
						onBlur={handleBlur}
						disabled={!canEditUser || isUpdatingUser}
						suffix={
							<EntityEditInputSuffix
								loading={isUpdatingUser}
								onConfirm={submitForm}
								onCancel={() => resetForm({
									values: {
										...values,
										userSurname: initialValues.current.userSurname,
									},
								})}
								showSuffix={dirtyFields.userSurname}
							/>
						}
					/>
				</div>

				<div className="flex flex-wrap gap-[16px]">
					<Input
						className="w-full"
						id="userEmail"
						type="email"
						label={t('re: email', 'E-mail')}
						disabled={isReddit() || isUpdatingUser}
						onChange={handleChange}
						value={values?.userEmail ?? ''}
						validationError={(touched.userEmail || touched.userEmailConfirmation)
							? errors.userEmail as string
							: undefined}
						onBlur={handleBlur}
					/>

					{
						(isSpotify() && dirtyFields.userEmail) && (
							<Input
								className="w-full"
								id="userEmailConfirmation"
								type="email"
								label={t('re: user creation email confirmation', 'Repeat e-mail')}
								onChange={handleChange}
								disabled={!canEditUser}
								value={values?.userEmailConfirmation ?? ''}
								validationError={(touched.userEmail || touched.userEmailConfirmation)
									? errors.userEmailConfirmation as string
									: undefined}
								onBlur={handleBlur}
								suffix={
									<EntityEditInputSuffix
										loading={isUpdatingUser}
										onConfirm={submitForm}
										onCancel={() => resetForm({
											values: {
												...values,
												userEmail: initialValues.current.userEmail,
											},
										})}
										showSuffix={!errors.userEmail}
									/>
								}
							/>
						)
					}
				</div>

				{
					isSpotify() && (
						<div className="flex flex-wrap gap-[16px]">
							<Input
								className="w-full"
								id="userPassword"
								label={t('sp: user password', 'Password')}
								onChange={handleChange}
								value={values.userPassword ?? ''}
								validationError={touched.userPassword ? errors.userPassword : undefined}
								onBlur={handleBlur}
								type="password"
							/>

							{
								dirtyFields.userPassword && (
									<Input
										className="w-full"
										id="userPasswordConfirmation"
										label={t('sp: user password confirmation', 'Password confirmation')}
										onChange={handleChange}
										value={values?.userPasswordConfirmation ?? ''}
										validationError={touched.userPasswordConfirmation ? errors.userPasswordConfirmation : undefined}
										onBlur={handleBlur}
										type="password"
										suffix={
											<EntityEditInputSuffix
												loading={isUpdatingUser}
												onConfirm={submitForm}
												onCancel={() => resetForm({
													values: {
														...values,
														userEmail: initialValues.current.userEmail,
													},
												})}
												showSuffix={!errors.userPassword}
											/>
										}
									/>
								)
							}
						</div>
					)
				}

				<div className="font-semibold text-[14px] pt-[16px]">
					{t('re: user permissions', 'PERMISSIONS')}
				</div>

				<div>
					{/* @ts-ignore: TODO: type issue in combobox */}
					<ComboBox
						value={values?.userAccountLevel ? getRedditUserLevelName(values?.userAccountLevel) : ''}
						disabled={!canEditUser || isUpdatingUser}
						size={ButtonSize.MEDIUM}
						triggerWrapperClassName="w-full"
						label={t('sc: role', 'Role')}
						inputSuffix={
							<EntityEditInputSuffix
								loading={isUpdatingUser}
								onConfirm={submitForm}
								onCancel={() => resetForm({
									values: {
										...values,
										userAccountLevel: initialValues.current.userAccountLevel,
									},
								})}
								showSuffix={dirtyFields.userAccountLevel && !errors.userAccountLevel}
							/>
						}
						items={roleOptions}
						selectType={MenuSelectType.SINGLE_SELECT}
						onSelect={(option) => {
							// TODO: combobox fires oneSelect before user actually selects an option
							// option then is SyntheticBaseEvent, so we need to check if event then dont setFieldValue

							if (option.constructor.name === 'SyntheticBaseEvent') {
								return;
							}

							setFieldValue('userAccountLevel', (option as IMenuOption)?.value);
						}}
						openMenuOnClick
					/>

					{errors?.userAccountLevel && touched?.userAccountLevel
						&& <div className="text-red-500 text-[10px]">{errors.userAccountLevel}</div>
					}
				</div>

				<div className="font-semibold text-[14px]">
					{t('re: user teams', 'TEAMS')}
				</div>

				<UsersTeamsAutocomplete
					disabled={!canEditUser || isUpdatingUser}
					onOptionClick={onInviteUserToGroup}
					values={values}
				/>

				<div>
					<div className="flex border-b text-[11px] border-gray-200 px-[8px] h-[32px] items-center
							text-gray-600"
					>
						<div className="flex-1">
							{t('re: user team', 'TEAM')}
						</div>
						<div className="w-[60px] flex justify-center">
							{t('re: user revoke', 'REVOKE')}
						</div>
					</div>
					{values.userGroups?.map((group) => (
						<GroupListItem
							group={group}
							user={mutationUser}
							onRevokeGroup={revokeUserGroup}
							hideActions={
								!isSpotify() && (
									!canUserEditGroup(group, currentPlatformUser)
										&& currentPlatformUser?.userAccountLevel !== UserAccountLevel.Admin
								)

							}
						/>
					))
					}

					{invitingUser
							&& <div className="flex h-[48px] px-[8px] gap-[8px] items-center justify-center">
								<Placeholder.Graph
									active
									className="!h-[32px] !w-[32px] min-w-[32px] !rounded-full"
								/>

								<Placeholder.Graph
									active
									className="!h-[16px]"
								/>
							</div>
					}
				</div>
			</div>
			<div className="flex-1 w-[25%] flex flex-col gap-[16px]">
				<div className="font-semibold text-[14px]">
					{t('re: user sidebar wise blue', 'WISE.BLUE')}
				</div>

				<div className="flex text-[12px]">
					<div className="w-[80px] flex flex-col gap-[8px] text-gray-600 font-semibold">
						<div>{t('re: user sidebar id', 'ID')}</div>
						<div>{t('re: user sidebar access', 'Access')}</div>
					</div>
					<div className="text-black flex flex-col gap-[8px]">
						<div className="text-gray-800">
							{values.userId ?? '-'}
						</div>
						<div>
							{values?.userId ? <AccessBadge
								// @ts-ignore
								accessEntities={userAccess}
							/> : '-'
							}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default RedditUsersEditMutator;
